import { useRef, useEffect, useLayoutEffect } from "react"
import { useDeepCompareEffect } from "ahooks"
import { Control, useWatch } from "react-hook-form"
import { BadgeTemplate } from "sharedTypes/badge"
import { useGetDocument } from "components/CanvasEditor/Editor"
import { useBadgeElement } from "./contexts/BadgeElementContext"
import { BadgeElementFormValues } from "./Panels/EditorPanel/BadgeSidePanelForm"
import { elementToFormValues, formValuesToElementUpdate } from "./utils/elementTransformers"

type Props<T> = {
  control: Control<BadgeElementFormValues>
  onChange?: (values: T) => void
  elementId?: string
  /** The badge's rules undid an edit (it didn't fit): the form should show the element again. */
  onUndone?: () => void
}

const AutoSave = <T, >({
  control, onChange, elementId, onUndone,
}: Props<T>) => {
  const { handleElementChange } = useBadgeElement()
  const getTemplate = useGetDocument<BadgeTemplate>()
  const watchedValues = useWatch({ control })
  const initialValuesRef = useRef<BadgeElementFormValues | null>(null)
  const lastElementIdRef = useRef<string | undefined>()
  const hasInitializedForElementRef = useRef(false)

  useEffect(() => {
    if (lastElementIdRef.current !== elementId) {
      lastElementIdRef.current = elementId
      hasInitializedForElementRef.current = false
      initialValuesRef.current = null
    }
  }, [elementId])

  useLayoutEffect(() => {
    if (!hasInitializedForElementRef.current && elementId && watchedValues) {
      initialValuesRef.current = watchedValues as BadgeElementFormValues
      hasInitializedForElementRef.current = true
    }
  }, [elementId, watchedValues])

  useDeepCompareEffect(() => {
    if (!hasInitializedForElementRef.current) {
      return
    }

    if (elementId && handleElementChange) {
      // useWatch returns partial type, but we know it's complete at this point
      const updatedChanges = formValuesToElementUpdate(watchedValues as BadgeElementFormValues)

      const currentElement = getTemplate().badgeData.elements.find((el) => el.id === elementId)

      // The form follows the canvas; when it matches the element, there's nothing to save.
      const unchanged = currentElement && JSON.stringify(updatedChanges)
        === JSON.stringify(formValuesToElementUpdate(elementToFormValues(currentElement)))
      if (unchanged) return
      handleElementChange(elementId, updatedChanges)
      // The badge's rules may have undone the edit: the form goes back to the element
      if (getTemplate().badgeData.elements.find((el) => el.id === elementId) === currentElement) {
        onUndone?.()
      }
    }

    if (onChange) {
      onChange(watchedValues as T)
    }
  }, [watchedValues])

  return null
}

export default AutoSave
